/**
 * Central registry for admin panel permissions.
 *
 * ADMIN_PANEL_PAGES is the single source of truth for both page access control
 * and the User Group permissions editor UI. The pagePermissionsStructure used
 * by the frontend is derived from ADMIN_PANEL_PAGES automatically.
 */

// ═══════════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════════

export type PagePermissionRow = {
  areaKey: string;
  label: string;
  labelKey?: string;
  viewTag?: string;
  editTag?: string;
  inverted?: boolean;
  // When any of these base-permission tags are active, the view permission is force-enabled and locked.
  impliedByTags?: readonly string[];
  isGroupHeader?: boolean;
  subRows?: PagePermissionRow[];
};

export type ApplicantToolRow = {
  areaKey: string;
  label: string;
  labelKey?: string;
  // viewTag is required for leaf rows. For group headers it is ignored and may be ''.
  viewTag: string;
  inverted?: boolean;
  prerequisiteLabelKey?: string;
  prerequisiteTags?: readonly string[];
  // When any of these base-permission tags are active, the tool is force-enabled and locked.
  impliedByTags?: readonly string[];
  // Group-header rows render an expand chevron + label and have no checkbox of their own.
  // Their sub-rows are independent leaves with their own viewTag and checkbox.
  isGroupHeader?: boolean;
  subRows?: ApplicantToolRow[];
  // Optional tooltip shown when hovering the row's checkbox or label.
  tooltipLabelKey?: string;
};

type PermTag = string | readonly string[];

export type PageLeaf = {
  view: PermTag;
  edit?: PermTag;
  labelKey?: string;
  impliedByTags?: readonly string[];
};

type PageGroup = {
  group: true;
  labelKey?: string;
  children: Record<string, PageLeaf>;
};

export type PageEntry = PageLeaf | PageGroup;

// ═══════════════════════════════════════════════════════════════════════════════
// Page Permission Registry
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Authoritative page registry. Groups nest their children directly.
 *
 * When `view`/`edit` is an array, the first element is the primary UI tag.
 * `edit` is omitted for pages that don't enforce edit-level auth.
 */
export const ADMIN_PANEL_PAGES = {
  'applicants': {
    group: true, labelKey: 'nav.applicants',
    children: {
      'all-applicants':   { view: 'all-applicants.view', edit: 'all-applicants.edit', labelKey: 'nav.all_applicants' },
      'applicant-groups': { view: 'applicant-groups.view', edit: 'applicant-groups.edit', labelKey: 'nav.applicant_groups' },
      'applicant-search': { view: 'applicant-search.view', edit: 'applicant-search.edit', labelKey: 'nav.applicant_search' },
    },
  },
  'dashboards':    { view: 'dashboards.view', edit: 'dashboards.edit', labelKey: 'nav.dashboards' },
  // Group key must differ from its child leaf key ('reports'), since both become
  // row `areaKey`s and the permissions table keys rows by areaKey (getRowId).
  'reports-group': {
    group: true, labelKey: 'nav.reports',
    children: {
      'reports':                { view: 'reports.view', edit: 'reports.edit', labelKey: 'nav.reports' },
      'reports-sensitive-data': { view: 'reports.sensitive-data.view', labelKey: 'user_groups.area_reports_sensitive_data' },
    },
  },
  'reviews': {
    group: true, labelKey: 'nav.reviews',
    children: {
      'my-reviews':    { view: 'my-reviews.view', labelKey: 'nav.my_reviews' },
      'review-queues': { view: ['reviews.view', 'reviewer'] as const, labelKey: 'nav.review_queues', impliedByTags: ['reviewer'] },
    },
  },
  'accounting': {
    group: true, labelKey: 'nav.accounting',
    children: {
      'payments':     { view: 'payments.view', labelKey: 'nav.payments' },
      'balances':     { view: 'balances.view', labelKey: 'nav.balances' },
      'transactions': { view: 'transactions.view', labelKey: 'nav.transactions' },
      'invoices':     { view: 'invoices.view', labelKey: 'nav.invoices' },
    },
  },
  'notifications': { view: 'notifications.view', labelKey: 'nav.notifications' },
  'user-management': {
    group: true, labelKey: 'nav.user_management',
    children: {
      'users':         { view: 'users.view', edit: ['users.edit', 'user_manager'] as const, labelKey: 'user_groups.area_users_and_user_groups' },
      'organizations': { view: ['organizations.view', 'user_manager'] as const, edit: ['organizations.edit', 'program_admin'] as const, labelKey: 'nav.organizations' },
    },
  },
  'settings': {
    group: true, labelKey: 'nav.settings',
    children: {
      'manage-fields':     { view: 'manage-fields.view', edit: 'manage-fields.edit', labelKey: 'user_groups.area_manage_fields' },
      'template-settings': { view: 'template-settings.view', edit: 'template-settings.edit', labelKey: 'user_groups.area_template_settings' },
    },
  },
  'audit-logs':    { view: 'audit-logs.view', labelKey: 'nav.audit_logs' },
  // Group key must differ from its child leaf key ('support'), since both become
  // row `areaKey`s and the permissions table keys rows by areaKey (getRowId).
  'support-group': {
    group: true, labelKey: 'nav.support',
    children: {
      'support':           { view: ['support.view', 'support'] as const, labelKey: 'user_groups.area_support_dashboards' },
      'support-templates': { view: 'support-templates.view', edit: 'support-templates.edit', labelKey: 'nav.support_templates' },
    },
  },
  // Allows view or edit access to the cross-program Global folder for saved /explore queries.
  'explore-global': { view: 'explore-global.view', edit: 'explore-global.edit', labelKey: 'user_groups.area_explore_global' },
} as const satisfies Record<string, PageEntry>;

/** Flat lookup of all leaf pages — used for access control. */
const PAGE_INDEX: Record<string, PageLeaf> = {};
for (const [key, entry] of Object.entries(ADMIN_PANEL_PAGES)) {
  if ('group' in entry) {
    for (const [k, child] of Object.entries(entry.children)) PAGE_INDEX[k] = child;
  } else {
    PAGE_INDEX[key] = entry;
  }
}

export type AdminPanelPageName = keyof typeof PAGE_INDEX;
export type AdminPanelAccessLevel = 'view' | 'edit';

// ═══════════════════════════════════════════════════════════════════════════════
// User Group Management (structures for the permission editor UI)
// ═══════════════════════════════════════════════════════════════════════════════

/** Base permission toggles shown above the page permissions table. */
export const basePermissions = [
  { tag: 'admin' },
  { tag: 'admin-panel' },
  { tag: 'reviewer' },
  { tag: 'screener' },
  { tag: 'internal audit' },
] as const;

/** Extracts the primary (first) tag from a PermTag value. */
const tag = (v: PermTag): string => typeof v === 'string' ? v : v[0];

const leaf = (key: string, e: PageLeaf): Omit<PagePermissionRow, 'label'> => ({
  areaKey: key, labelKey: e.labelKey, viewTag: tag(e.view), editTag: e.edit ? tag(e.edit) : undefined,
  ...(e.impliedByTags ? { impliedByTags: e.impliedByTags } : {}),
});

/** Page-permission table structure. Derived from ADMIN_PANEL_PAGES. Labels resolved at render time via i18n. */
export const pagePermissionsStructure: Omit<PagePermissionRow, 'label'>[] =
  Object.entries(ADMIN_PANEL_PAGES)
    .map(([key, entry]) =>
      'group' in entry
        ? { areaKey: key, labelKey: entry.labelKey, isGroupHeader: true,
          subRows: Object.entries(entry.children).map(([k, e]) =>
            ({ ...leaf(k, e), label: '' })) }
        : leaf(key, entry)
    );

/** Applicant-tool toggles table structure. Labels resolved at render time via i18n.
 *  Sub-rows carry `label: ''` as a placeholder so the type matches ApplicantToolRow
 *  (mirrors the pattern used by pagePermissionsStructure above). */
export const applicantToolsStructure: Omit<ApplicantToolRow, 'label'>[] = [
  // Inverted tools (enabled by default — tag removes access)
  { areaKey: 'tool-notes', viewTag: 'no-notes', inverted: true, labelKey: 'user_groups.area_tool_notes' },
  { areaKey: 'tool-comms', viewTag: 'no-comms', inverted: true, labelKey: 'user_groups.area_tool_comms',
    impliedByTags: ['support.view'] },
  { areaKey: 'tool-history', viewTag: 'no-history', inverted: true, labelKey: 'user_groups.area_tool_history' },
  { areaKey: 'tool-debug', viewTag: 'no-debug', inverted: true, labelKey: 'user_groups.area_tool_debug' },
  { areaKey: 'tool-meetings', viewTag: 'no-meetings', inverted: true,
    labelKey: 'user_groups.area_tool_meetings' },
  { areaKey: 'tool-ia-notes', viewTag: 'no-internal audit', inverted: true,
    labelKey: 'user_groups.area_tool_ia_notes',
    prerequisiteLabelKey: 'user_groups.prerequisite_ia_notes',
    prerequisiteTags: ['internal audit'] },
  { areaKey: 'tool-fraud', viewTag: 'no-fraud', inverted: true,
    labelKey: 'user_groups.area_tool_fraud',
    prerequisiteLabelKey: 'user_groups.prerequisite_fraud',
    prerequisiteTags: ['admin', 'reviewer'] },
  // Grant tools (disabled by default — tag grants access)
  { areaKey: 'tool-payments', viewTag: '', labelKey: 'user_groups.area_tool_payments', isGroupHeader: true,
    subRows: [
      { areaKey: 'tool-payments-view', viewTag: 'all-applicants.view_payments', labelKey: 'user_groups.area_tool_view_payments',
        impliedByTags: ['admin', 'reviewer'], label: '' },
      { areaKey: 'tool-payments-pause', viewTag: 'all-applicants.pause_payments', labelKey: 'user_groups.area_tool_payment_actions',
        tooltipLabelKey: 'user_groups.area_tool_payment_actions_tooltip', impliedByTags: ['admin'], label: '' },
    ] },
  { areaKey: 'tool-resend-subsurveys', viewTag: 'resend_subsurveys', labelKey: 'user_groups.area_tool_resend_subsurveys',
    impliedByTags: ['admin', 'admin-panel'] },
  { areaKey: 'tool-view-filehub', viewTag: 'view_filehub', labelKey: 'user_groups.area_tool_view_filehub' },
  { areaKey: 'tool-view-activity-log', viewTag: 'view_activity_log', labelKey: 'user_groups.area_tool_view_activity_log' },
  { areaKey: 'tool-third-party-checks', viewTag: 'view_third_party_checks',
    labelKey: 'user_groups.area_tool_third_party_checks' },
  { areaKey: 'tool-review-assistant', viewTag: 'ai_coach', labelKey: 'user_groups.area_tool_review_assistant' }
];

// ═══════════════════════════════════════════════════════════════════════════════
// Page Access Control (enforcement logic)
// ═══════════════════════════════════════════════════════════════════════════════

/** Admin panel super-user — bypasses all permissions within the admin panel. */
export const ADMIN_PANEL_BYPASS_PERMISSIONS = ['admin', 'admin-panel'] as const;

/** Returns true if the given permissions include any admin-level bypass permission. */
export const hasAdminTag = (permissions: string[]): boolean =>
  ADMIN_PANEL_BYPASS_PERMISSIONS.some(p => permissions.includes(p));

/** Roles that can view ALL organizations (not scoped to their own org). */
export const ALL_ORGS_ROLES = ['admin', 'admin-panel', 'program_admin', 'organizations.view', 'organizations.edit'] as const;

/**
 * Resolves page+level to the array of permission strings that grant access (OR logic).
 * 'view' → [page.view, page.edit] (edit implies view)
 * 'edit' → [page.edit]
 * Array of pages → union across all pages (access to ANY page suffices).
 */
export function resolvePagePermissions(
  page: AdminPanelPageName | AdminPanelPageName[],
  level: AdminPanelAccessLevel
): string[] {
  const pages = Array.isArray(page) ? page : [page];
  const perms = new Set<string>();
  const addPerms = (val: string | readonly string[]) => {
    if (typeof val === 'string') perms.add(val);
    else val.forEach(v => perms.add(v));
  };
  for (const p of pages) {
    const pageDef = PAGE_INDEX[p];
    if (level === 'edit') {
      if ('edit' in pageDef) addPerms(pageDef.edit!);
    } else {
      addPerms(pageDef.view);
      if ('edit' in pageDef) addPerms(pageDef.edit!);
    }
  }
  return [...perms];
}

/**
 * Full access check: bypass perms + page resolution.
 * Returns true if the user has any bypass permission OR any of the resolved page permissions.
 */
export function checkPageAccess(
  userPerms: string[] | undefined,
  page: AdminPanelPageName | AdminPanelPageName[],
  level: AdminPanelAccessLevel
): boolean {
  if (!userPerms) return false;
  if (ADMIN_PANEL_BYPASS_PERMISSIONS.some(p => userPerms.includes(p))) return true;
  const required = resolvePagePermissions(page, level);
  return required.some(p => userPerms.includes(p));
}

/** Flat lookup of applicant tools by their viewTag (group headers with empty viewTag are skipped). */
const APPLICANT_TOOL_INDEX: Record<string, Omit<ApplicantToolRow, 'label'>> = {};
const indexApplicantTool = (tool: Omit<ApplicantToolRow, 'label'>) => {
  if (tool.viewTag) APPLICANT_TOOL_INDEX[tool.viewTag] = tool;
  tool.subRows?.forEach(indexApplicantTool);
};
for (const tool of applicantToolsStructure) indexApplicantTool(tool);

/**
 * Access check for an applicant tool.
 *
 * Grant-type tools (the default): access requires an admin bypass, the tool's view tag,
 * or any of the tool's impliedByTags.
 *
 * Inverted tools (e.g. 'no-fraud'): enabled by default, where the tag *removes* access —
 * so access is granted as long as the user does NOT hold the tag.
 */
export function checkApplicantToolAccess(
  userPerms: string[] | undefined,
  toolViewTag: string
): boolean {
  if (!userPerms) return false;
  if (ADMIN_PANEL_BYPASS_PERMISSIONS.some(p => userPerms.includes(p))) return true;
  const tool = APPLICANT_TOOL_INDEX[toolViewTag];
  if (tool?.inverted) return !userPerms.includes(toolViewTag);
  if (userPerms.includes(toolViewTag)) return true;
  return tool?.impliedByTags?.some(t => userPerms.includes(t)) ?? false;
}

/**
 * Returns a new permission list with EVERY applicant tool selected, to match the
 * "admin bypasses everything" enforcement semantics of {@link checkApplicantToolAccess}.
 *
 * Use this when an admin-bypass base permission (admin / admin-panel) is added in the
 * User Group editor. Inverted tools (default-on, where the tag *removes* access) get
 * their restriction tag removed; grant tools (default-off, where the tag *grants*
 * access) get their grant tag added. This keeps the editor UI consistent with the fact
 * that an admin already has access to all applicant tools at enforcement time.
 *
 * Non-applicant-tool permissions are preserved untouched.
 */
export function applyAdminBypassToApplicantTools(permissions: string[]): string[] {
  const leaves = applicantToolsStructure.flatMap(t =>
    t.isGroupHeader ? (t.subRows ?? []) : [t]
  );
  const invertedTags = new Set(leaves.filter(t => t.inverted).map(t => t.viewTag));
  const grantTags = leaves
    .filter(t => !t.inverted && t.viewTag)
    .map(t => t.viewTag);

  // Remove inverted restriction tags so default-on tools read as enabled.
  let next = permissions.filter(p => !invertedTags.has(p));
  // Add grant tags so default-off tools read as selected (dedup via Set).
  next = Array.from(new Set([...next, ...grantTags]));
  return next;
}

/** Every page-permission tag string from ADMIN_PANEL_PAGES + applicantToolsStructure (deduplicated). Used by tags.ts. */
export const allPagePermissionTagStrings: string[] = (() => {
  const tags = new Set<string>();

  const addVal = (v: string | readonly string[]) => {
    if (typeof v === 'string') tags.add(v);
    else v.forEach(s => tags.add(s));
  };

  // From the page access control registry
  for (const pageDef of Object.values(PAGE_INDEX)) {
    addVal(pageDef.view);
    if (pageDef.edit) addVal(pageDef.edit);
  }

  // From the applicant tools structure (including grouped sub-rows)
  const collectToolTags = (tool: Omit<ApplicantToolRow, 'label'>) => {
    if (tool.viewTag) tags.add(tool.viewTag);
    tool.subRows?.forEach(collectToolTags);
  };
  for (const tool of applicantToolsStructure) collectToolTags(tool);

  return [...tags];
})();
